import { Spinner, Text, makeStyles, tokens, Divider, Title3 } from '@fluentui/react-components';
import { TimelineEvent } from './TimelineEvent';
import type { LogEntry } from '../../types';

// Ordenacao de eventos agora eh feita no backend (LogService.cs)
// O backend ordena por ciclo de processamento e prioridade da operacao

interface EventTimelineProps {
  correlationId: string;
  events: LogEntry[];
  loading?: boolean;
  statusFilter?: string;  // Não usado no timeline, mantido para compatibilidade
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  correlationIdContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  correlationId: {
    fontFamily: 'monospace',
    fontSize: '14px',
    padding: '4px 8px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  stats: {
    display: 'flex',
    gap: '16px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  statValue: {
    fontSize: '18px',
    fontWeight: 600,
  },
  timelineContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  emptyState: {
    textAlign: 'center',
    padding: '32px',
    color: tokens.colorNeutralForeground3,
  },
  copySuccess: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '12px',
  },
});

export const EventTimeline: React.FC<EventTimelineProps> = ({
  events,
  loading = false,
}) => {
  const styles = useStyles();

  // Backend ja retorna eventos ordenados por ciclo de processamento e prioridade
  const sortedEvents = events;

  const getStats = () => {
    const errorCount = sortedEvents.filter(e => e.level === 'Error').length;
    const warningCount = sortedEvents.filter(e => e.level === 'Warning').length;
    const infoCount = sortedEvents.filter(e => e.level === 'Information').length;

    // Usa OperationTimestamp se disponível, caso contrário TimeGenerated
    const getTimestamp = (event: LogEntry) => {
      const timestamp = event.operationTimestamp || event.timeGenerated;
      return new Date(timestamp);
    };

    const startTime = sortedEvents.length > 0 ? getTimestamp(sortedEvents[0]) : null;
    const endTime = sortedEvents.length > 0 ? getTimestamp(sortedEvents[sortedEvents.length - 1]) : null;

    let duration = '-';
    if (startTime && endTime) {
      const durationMs = endTime.getTime() - startTime.getTime();
      if (durationMs < 1000) {
        duration = `${durationMs}ms`;
      } else if (durationMs < 60000) {
        duration = `${(durationMs / 1000).toFixed(2)}s`;
      } else {
        duration = `${(durationMs / 60000).toFixed(2)}min`;
      }
    }

    return { errorCount, warningCount, infoCount, duration };
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Spinner label="Carregando timeline de eventos..." size="medium" />
      </div>
    );
  }

  if (sortedEvents.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text size={400}>Nenhum evento encontrado para este CorrelationId</Text>
        </div>
      </div>
    );
  }

  const stats = getStats();

  return (
    <div className={styles.container}>
      {/* Estatísticas */}
      <div className={styles.stats}>
        <div className={styles.statItem}>
          <Text className={styles.statLabel}>Total de Eventos</Text>
          <Text className={styles.statValue}>{sortedEvents.length}</Text>
        </div>
        <div className={styles.statItem}>
          <Text className={styles.statLabel}>Duração</Text>
          <Text className={styles.statValue}>{stats.duration}</Text>
        </div>
        <div className={styles.statItem}>
          <Text className={styles.statLabel}>Erros</Text>
          <Text className={styles.statValue} style={{ color: stats.errorCount > 0 ? tokens.colorPaletteRedForeground1 : undefined }}>
            {stats.errorCount}
          </Text>
        </div>
        <div className={styles.statItem}>
          <Text className={styles.statLabel}>Avisos</Text>
          <Text className={styles.statValue} style={{ color: stats.warningCount > 0 ? tokens.colorPaletteYellowForeground1 : undefined }}>
            {stats.warningCount}
          </Text>
        </div>
        <div className={styles.statItem}>
          <Text className={styles.statLabel}>Informações</Text>
          <Text className={styles.statValue}>{stats.infoCount}</Text>
        </div>
      </div>

      <Divider />

      {/* Timeline de eventos */}
      <div className={styles.timelineContainer}>
        <Title3 style={{ marginBottom: tokens.spacingVerticalM }}>
          Timeline de Eventos
        </Title3>
        {sortedEvents.map((event, index) => {
          // Pega nome e email do primeiro evento (sempre o mesmo para toda a timeline)
          const displayName = sortedEvents[0]?.displayName || event.displayName || '-';
          const userPrincipalName = sortedEvents[0]?.userPrincipalName || event.userPrincipalName || '-';

          return (
            <TimelineEvent
              key={`${event.correlationId}-${index}`}
              event={{...event, displayName, userPrincipalName}}
              isFirstEvent={index === 0}
            />
          );
        })}
      </div>
    </div>
  );
};
